import { useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { shell } from '../../shell/shellClasses';
import { formatInt, formatMoney } from '../daily-brief/format';
import { hourDeepLink, metricValue } from './buildHourly';
import type { PulseMetricId, SalesPulseModel, PulseSeriesId } from './types';
import { PULSE_SERIES } from './types';

const W = 720;
const H = 260;
const PL = 40;
const PR = 12;
const PT = 16;
const PB = 28;

function domainMax(
  model: SalesPulseModel,
  metric: PulseMetricId,
  seriesOn: Set<PulseSeriesId>,
): number {
  let max = 0;
  for (const p of model.points) {
    for (const s of PULSE_SERIES) {
      if (!seriesOn.has(s.id)) continue;
      const v = metricValue(p, s.id, metric);
      if (v != null && v > max) max = v;
    }
  }
  return max > 0 ? max * 1.1 : 1;
}

export function HourlyBarChart({
  model,
  metric,
  seriesOn,
}: {
  model: SalesPulseModel;
  metric: PulseMetricId;
  seriesOn: Set<PulseSeriesId>;
}) {
  const navigate = useNavigate();
  const uid = useId();
  const [focus, setFocus] = useState(0);
  const [hover, setHover] = useState<number | null>(null);
  const activeHour = hover ?? focus;
  const plotW = W - PL - PR;
  const plotH = H - PT - PB;
  const maxY = useMemo(() => domainMax(model, metric, seriesOn), [model, metric, seriesOn]);
  const barW = Math.max(4, (plotW / 24) * 0.55);
  const step = plotW / 24;

  const active = model.points[Math.min(Math.max(activeHour, 0), 23)];

  const openHour = (hour: number) => {
    navigate(
      hourDeepLink({
        connectionId: model.connectionId,
        dayIso: model.dayIso,
        hour,
        marketplaceId: model.marketplaceId,
      }),
    );
  };

  const yOf = (v: number | null) => {
    if (v == null || maxY <= 0) return PT + plotH;
    return PT + plotH - (v / maxY) * plotH;
  };

  const linePath = (series: PulseSeriesId) => {
    const pts: string[] = [];
    for (const p of model.points) {
      const v = metricValue(p, series, metric);
      if (v == null) continue;
      const x = PL + p.hour * step + step / 2;
      const y = yOf(v);
      pts.push(`${x},${y}`);
    }
    return pts.length ? pts.join(' ') : '';
  };

  const formatVal = (v: number | null) => {
    if (v == null) return 'N/A';
    if (metric === 'gmv') return formatMoney(v, model.currency);
    if (metric === 'orders') return formatInt(Math.round(v));
    return formatInt(Math.round(v));
  };

  return (
    <div className="relative w-full">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full max-h-[300px]"
        role="img"
        aria-labelledby={`${uid}-title ${uid}-desc`}
        tabIndex={0}
        style={{ touchAction: 'manipulation' }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            setHover(null);
            setFocus((h) => Math.min(23, h + 1));
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            setHover(null);
            setFocus((h) => Math.max(0, h - 1));
          } else if (e.key === 'Enter') {
            openHour(activeHour);
          }
        }}
      >
        <title id={`${uid}-title`}>24 hour sales pulse</title>
        <desc id={`${uid}-desc`}>
          Hourly {metric} for {model.dayLabel}. Arrow keys inspect hours; Enter opens Orders for that
          hour.
        </desc>

        {[0, 0.5, 1].map((t) => {
          const y = PT + plotH * (1 - t);
          return (
            <line
              key={t}
              x1={PL}
              x2={W - PR}
              y1={y}
              y2={y}
              stroke="var(--shell-border)"
              strokeWidth={1}
              opacity={0.7}
            />
          );
        })}

        {seriesOn.has('today') &&
          model.points.map((p) => {
            const v = metricValue(p, 'today', metric) ?? 0;
            const x = PL + p.hour * step + (step - barW) / 2;
            const y = yOf(v);
            const height = PT + plotH - y;
            const selected = p.hour === activeHour;
            return (
              <rect
                key={p.hour}
                x={x}
                y={y}
                width={barW}
                height={Math.max(0, height)}
                fill="var(--shell-accent)"
                opacity={selected ? 1 : 0.75}
                rx={2}
                className="cursor-pointer motion-safe:transition-opacity duration-[var(--shell-motion-fast)]"
                onPointerEnter={() => setHover(p.hour)}
                onPointerLeave={() => setHover(null)}
                onClick={() => openHour(p.hour)}
              >
                <title>
                  {p.label} · {formatVal(metricValue(p, 'today', metric))}
                </title>
              </rect>
            );
          })}

        {PULSE_SERIES.filter((s) => s.kind === 'line' && seriesOn.has(s.id)).map((s) => {
          const pts = linePath(s.id);
          if (!pts) return null;
          return (
            <polyline
              key={s.id}
              fill="none"
              stroke={s.color}
              strokeWidth={1.75}
              strokeDasharray={s.id === 'yesterday' ? undefined : '4 3'}
              strokeLinejoin="round"
              strokeLinecap="round"
              points={pts}
              pointerEvents="none"
              opacity={0.9}
            />
          );
        })}

        {/* x ticks */}
        {[0, 6, 12, 18, 23].map((h) => (
          <text
            key={h}
            x={PL + h * step + step / 2}
            y={H - 8}
            textAnchor="middle"
            fill="var(--shell-fg-subtle)"
            fontSize={10}
          >
            {String(h).padStart(2, '0')}
          </text>
        ))}

        {active && (
          <line
            x1={PL + active.hour * step + step / 2}
            x2={PL + active.hour * step + step / 2}
            y1={PT}
            y2={PT + plotH}
            stroke="var(--shell-fg-muted)"
            strokeWidth={1}
            strokeDasharray="2 2"
            opacity={0.5}
            pointerEvents="none"
          />
        )}
      </svg>

      {active && (
        <div
          role="status"
          className={`mt-2 rounded-[var(--shell-radius-md)] border ${shell.border} ${shell.surfaceElevated} px-3 py-2 text-xs`}
        >
          <p className="font-medium text-[var(--shell-fg)]">
            {active.label}{' '}
            <span className={shell.muted}>
              ({model.dayIso})
              {model.sellerRef ? ` · ${model.sellerRef}` : ''}
            </span>
          </p>
          <ul className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 sm:grid-cols-3">
            <li className="flex justify-between gap-2 tabular-nums">
              <span>GMV</span>
              <span>{formatMoney(active.today.gmv, model.currency)}</span>
            </li>
            <li className="flex justify-between gap-2 tabular-nums">
              <span>Order items</span>
              <span>{formatInt(active.today.orders)}</span>
            </li>
            <li className="flex justify-between gap-2 tabular-nums">
              <span>Units</span>
              <span>{formatInt(active.today.units)}</span>
            </li>
            {seriesOn.has('yesterday') && (
              <li className="flex justify-between gap-2 tabular-nums">
                <span className="text-[#38bdf8]">Yesterday ({metric})</span>
                <span>{formatVal(metricValue(active, 'yesterday', metric))}</span>
              </li>
            )}
            {seriesOn.has('avg7') && (
              <li className="flex justify-between gap-2 tabular-nums">
                <span className="text-[#a78bfa]">7d avg</span>
                <span>{formatVal(metricValue(active, 'avg7', metric))}</span>
              </li>
            )}
            {seriesOn.has('avg15') && (
              <li className="flex justify-between gap-2 tabular-nums">
                <span className="text-[#34d399]">15d avg</span>
                <span>{formatVal(metricValue(active, 'avg15', metric))}</span>
              </li>
            )}
            {seriesOn.has('avg30') && (
              <li className="flex justify-between gap-2 tabular-nums">
                <span className="text-[#94a3b8]">30d avg</span>
                <span>{formatVal(metricValue(active, 'avg30', metric))}</span>
              </li>
            )}
          </ul>
          <p className={`mt-1 ${shell.subtle}`}>
            Click bar or press Enter to open Orders for this hour.
          </p>
        </div>
      )}
    </div>
  );
}
